import React from 'react';
import { Facebook, Globe, Youtube, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SocialLinks {
  tiktok?: string;
  facebook?: string;
  website?: string;
  youtube?: string;
  instagram?: string;
}

interface DisplayConnectLinksProps {
  // Legacy props for backwards compatibility
  facebook?: string | null;
  website?: string | null;
  // New props for social links
  socialLinks?: SocialLinks;
  className?: string;
  variant?: 'buttons' | 'icons'; // buttons = old style, icons = new ArtStation style
}

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const DisplayConnectLinks: React.FC<DisplayConnectLinksProps> = ({
  facebook,
  website,
  socialLinks,
  className = '',
  variant = 'icons'
}) => {
  // Merge legacy props with new socialLinks object
  const allLinks = {
    facebook: socialLinks?.facebook || facebook || '',
    website: socialLinks?.website || website || '',
    tiktok: socialLinks?.tiktok || '',
    youtube: socialLinks?.youtube || '',
    instagram: socialLinks?.instagram || ''
  };

  // Filter out empty links
  const validLinks = Object.entries(allLinks).filter(([_, url]) => url && url.trim() !== '');

  if (validLinks.length === 0) return null;

  // Legacy button style for backwards compatibility
  if (variant === 'buttons') {
    return (
      <div className={`space-y-2 ${className}`}>
        {allLinks.facebook && (
          <a
            href={allLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full"
          >
            <Button
              variant="outline"
              className="w-full justify-start text-sm"
              size="sm"
            >
              <Facebook className="h-4 w-4 mr-2" />
              Facebook
            </Button>
          </a>
        )}

        {allLinks.website && (
          <a
            href={allLinks.website}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full"
          >
            <Button
              variant="outline"
              className="w-full justify-start text-sm"
              size="sm"
            >
              <Globe className="h-4 w-4 mr-2" />
              Website
            </Button>
          </a>
        )}
      </div>
    );
  }

  // New icon style (ArtStation-inspired)
  return (
    <TooltipProvider>
      <div className={`flex items-center gap-3 ${className}`}>
        {allLinks.website && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={allLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Globe className="h-4 w-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>Website</p>
            </TooltipContent>
          </Tooltip>
        )}

        {allLinks.facebook && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={allLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>Facebook</p>
            </TooltipContent>
          </Tooltip>
        )}

        {allLinks.youtube && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={allLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
              >
                <Youtube className="h-4 w-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>YouTube</p>
            </TooltipContent>
          </Tooltip>
        )}

        {allLinks.instagram && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={allLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-pink-50 hover:text-pink-600 hover:border-pink-200 transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>Instagram</p>
            </TooltipContent>
          </Tooltip>
        )}

        {allLinks.tiktok && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={allLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-gray-50 hover:text-gray-900 hover:border-gray-200 transition-colors"
              >
                <TikTokIcon className="h-4 w-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>TikTok</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default DisplayConnectLinks;